/**
 * useChatComposerInput — composer input plumbing lifted out of ChatBox
 * (delegation pattern). The three useEffects (name-focus, refocus-on-stream-end,
 * resize-on-input) keep their byte-identical dependency arrays, and the plain
 * functions (resizeTextarea, handleNameClick/Save/KeyDown, handleKeyDown for
 * command-autocomplete nav) are reproduced VERBATIM from ChatBox.
 *
 * The three effects are the only hooks here and were all contiguous and ahead
 * of useChatCommands/useChatSubmit in ChatBox, so calling this hook at the same
 * position preserves hook count/order exactly. The name/command-autocomplete
 * state stays as useState in the orchestrator and is threaded in by reference;
 * refs are threaded in too. handleKeyDown is a plain (non-hook) function so its
 * original position relative to the command hooks does not affect hook order.
 */
import { useEffect } from 'react';
import type { Dispatch, RefObject, SetStateAction } from 'react';
import type { Conversation } from '@/types/conversations';
import type { CommandInfo } from '@/features/chat/utils/command-parser';

export interface UseChatComposerInputDeps {
  isEditingName: boolean;
  setIsEditingName: Dispatch<SetStateAction<boolean>>;
  setUserName: Dispatch<SetStateAction<string>>;
  isMobile: boolean;
  isStreaming: boolean;
  isLoading: boolean;
  activeConversation: Conversation | null;
  inputValue: string;
  setInputValue: Dispatch<SetStateAction<string>>;
  nameInputRef: RefObject<HTMLInputElement | null>;
  chatInputRef: RefObject<HTMLTextAreaElement | null>;
  showCommandDropdown: boolean;
  commandMatches: CommandInfo[];
  selectedCommandIndex: number;
  setShowCommandDropdown: Dispatch<SetStateAction<boolean>>;
  setCommandMatches: Dispatch<SetStateAction<CommandInfo[]>>;
  setSelectedCommandIndex: Dispatch<SetStateAction<number>>;
  // @-mention autocomplete keyboard nav (issue #1457). Handled ahead of the
  // command dropdown so an open mention list wins ArrowUp/Down/Enter/Escape.
  showMentionDropdown: boolean;
  mentionMatchCount: number;
  setSelectedMentionIndex: Dispatch<SetStateAction<number>>;
  selectMention: (index?: number) => void;
  closeMentionDropdown: () => void;
}

export interface UseChatComposerInputResult {
  resizeTextarea: () => void;
  handleNameClick: () => void;
  handleNameSave: () => void;
  handleNameKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  handleKeyDown: (e: React.KeyboardEvent) => void;
}

export function useChatComposerInput(deps: UseChatComposerInputDeps): UseChatComposerInputResult {
  const {
    isEditingName,
    setIsEditingName,
    setUserName,
    isMobile,
    isStreaming,
    isLoading,
    activeConversation,
    inputValue,
    setInputValue,
    nameInputRef,
    chatInputRef,
    showCommandDropdown,
    commandMatches,
    selectedCommandIndex,
    setShowCommandDropdown,
    setCommandMatches,
    setSelectedCommandIndex,
    showMentionDropdown,
    mentionMatchCount,
    setSelectedMentionIndex,
    selectMention,
    closeMentionDropdown,
  } = deps;

  // Focus input when editing name starts
  useEffect(() => {
    if (isEditingName && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [isEditingName]);

  // Refocus chat input when streaming/loading completes (desktop only — on mobile this pops keyboard)
  useEffect(() => {
    if (isMobile) return;
    if (!isStreaming && !isLoading && activeConversation) {
      // Small delay to ensure the input is enabled before focusing
      setTimeout(() => {
        chatInputRef.current?.focus();
      }, 50);
    }
  }, [isMobile, isStreaming, isLoading, activeConversation]);

  // Auto-resize textarea to fit content: grow with the text and only show a
  // scrollbar once we hit the CSS max-height (otherwise overflow stays hidden so
  // the field scales cleanly — important for the narrow side-chat pane).
  const resizeTextarea = () => {
    const textarea = chatInputRef.current;
    if (!textarea) return;
    // Reset height to auto so scrollHeight reflects the true content height.
    textarea.style.height = 'auto';
    // Single source of truth for the cap is the CSS max-height.
    const maxHeight = parseFloat(getComputedStyle(textarea).maxHeight) || Infinity;
    const next = textarea.scrollHeight;
    if (next > maxHeight) {
      textarea.style.height = `${maxHeight}px`;
      textarea.style.overflowY = 'auto'; // at the cap → scroll
    } else {
      textarea.style.height = `${next}px`;
      textarea.style.overflowY = 'hidden'; // still growing → no scrollbar
    }
  };

  // Resize textarea when input changes
  useEffect(() => {
    resizeTextarea();
  }, [inputValue]);

  const handleNameClick = () => {
    setIsEditingName(true);
  };

  const handleNameSave = () => {
    const newName = nameInputRef.current?.value.trim() || 'You';
    setUserName(newName);
    // Note: Name changes here are session-only. Use Profile Settings for persistent changes.
    setIsEditingName(false);
  };

  const handleNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleNameSave();
    } else if (e.key === 'Escape') {
      setIsEditingName(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Handle @-mention autocomplete navigation FIRST (issue #1457). When the
    // mention dropdown is open it owns the arrow/enter/escape keys so the
    // command dropdown and plain-Enter-submit never fire under it.
    if (showMentionDropdown) {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeMentionDropdown();
        return;
      }
      if (mentionMatchCount > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setSelectedMentionIndex(prev => (prev < mentionMatchCount - 1 ? prev + 1 : 0));
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setSelectedMentionIndex(prev => (prev > 0 ? prev - 1 : mentionMatchCount - 1));
          return;
        }
        if (e.key === 'Enter' || e.key === 'Tab') {
          e.preventDefault();
          selectMention();
          return;
        }
      }
    }

    // Handle command autocomplete navigation
    if (showCommandDropdown && commandMatches.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedCommandIndex(prev =>
          prev < commandMatches.length - 1 ? prev + 1 : 0
        );
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedCommandIndex(prev =>
          prev > 0 ? prev - 1 : commandMatches.length - 1
        );
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        const selected = commandMatches[selectedCommandIndex];
        if (selected) {
          setShowCommandDropdown(false);
          setCommandMatches([]);
          if (selected.hasArgs) {
            // Command takes arguments - add space for user to type
            setInputValue('/' + selected.name + ' ');
          } else {
            // Command has no args - submit immediately
            setInputValue('/' + selected.name);
            // Trigger submit after state update
            setTimeout(() => {
              const form = chatInputRef.current?.closest('form');
              form?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
            }, 0);
          }
        }
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowCommandDropdown(false);
        return;
      }
    }

    // Regular enter to submit (allow during streaming - message will be queued)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      const form = (e.target as HTMLElement).closest('form');
      if (form) {
        form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      }
    }
  };

  return {
    resizeTextarea,
    handleNameClick,
    handleNameSave,
    handleNameKeyDown,
    handleKeyDown,
  };
}
